// Generated by GitHub Copilot
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { TOAST_EVENT } from '#core/notifications/toast.ts'
import type { ToastPayload } from '#core/notifications/toast.ts'
import { cn } from '#core/utils/cn.ts'

/**
 * Global toast notification host. Mount exactly once at the app root.
 *
 * Listens to `ynteq:toast` window events emitted by {@link showToast} and renders
 * dismissable alert toasts fixed at the top-right corner of the screen.
 * Each toast auto-dismisses after its configured duration.
 */
export function ToastContainer() {
  const { t } = useTranslation('general')
  const [toasts, setToasts] = useState<ToastPayload[]>([])

  useEffect(() => {
    const handler = (e: Event) => {
      const payload = (e as CustomEvent<ToastPayload>).detail
      setToasts((prev) => [...prev, payload])
      setTimeout(() => {
        setToasts((prev) => prev.filter((item) => item.id !== payload.id))
      }, payload.duration)
    }

    window.addEventListener(TOAST_EVENT, handler)
    return () => window.removeEventListener(TOAST_EVENT, handler)
  }, [])

  if (toasts.length === 0) return null

  return (
    <div
      className="ynt-toast-stack"
      style={{ zIndex: 1090 }}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn('alert', `alert-${toast.variant}`, 'd-flex align-items-start gap-2 shadow-sm mb-0')}
          role="alert"
          style={{ minWidth: '280px', maxWidth: '420px' }}
        >
          <span className="flex-grow-1 text-start">{toast.message}</span>
          <button
            type="button"
            className="btn-close flex-shrink-0"
            style={{ fontSize: '0.75em', marginTop: '0.2em' }}
            aria-label={t('shared.common.dismiss')}
            onClick={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
          />
        </div>
      ))}
    </div>
  )
}
